import React, { useMemo } from 'react';
import { useIntl, defineMessages, IntlShape } from 'react-intl';
import { Music24PlanTier, MUSIC24_TIER_RANK } from 'fym-common/src/license/music24Plans';
import { TypedMemo } from '../../../../common/types';
import { useMusic24Plan } from '../../../hooks/useMusic24Plan';
import { useMyTeamRole } from '../../../../team/useMyTeamRole';
import { useWorkspaceOwner } from '../../../../team/useWorkspaceOwner';
import { planDescriptionMessages, planNameMessages } from '../../../config/music24PlanNames';
import { BillingInterval, MUSIC24_PLANS, Music24Plan, Music24PlanId } from '../../../config/music24StripePlans';
import { PLAN_BUILDS_ON, planHighlights } from '../../../plans/planHighlights';
import { PricingPlanCard } from './PricingPlanCard';
import { PricingFreeRow } from './PricingFreeRow';
import { PricingCompareTable } from './PricingCompareTable';
import { PricingMemberNotice } from './PricingMemberNotice';
import { PricingContactLine } from './PricingContactLine';
import { useIsNativeEmbed } from '../../../navigation/hooks/useIsNativeEmbed';
import { NativeUpgradeNote } from '../../NativeUpgradeNote/NativeUpgradeNote';
import styles from './PricingPlansStep.module.scss';

const messages = defineMessages({
  yourPlan: { id: 'app.sml.pricing.button.yourPlan', defaultMessage: 'Your plan' },
  upgradeTo: { id: 'app.sml.pricing.button.upgradeTo', defaultMessage: 'Upgrade to {plan}' },
  switchTo: { id: 'app.sml.pricing.button.switchTo', defaultMessage: 'Switch to {plan}' },
  lead: { id: 'app.sml.plans.card.lead', defaultMessage: 'Everything in {plan}, plus' },
});

export interface PricingPlansStepProps {
  interval: BillingInterval;
  loadingPlanId: string | null;
  /** The checkout of a plan for one artist. Extra artists are sold only in Settings -> Subscription. */
  onSubscribe: (plan: Music24Plan) => void;
  /** The plan a website "Choose <plan>" button named (ruling C2) — that card gets the featured
   * treatment and the primary button, on top of whatever `isCurrent`/`mostPopular` already give
   * it. `null`/omitted when the visitor did not arrive with a checkout intent. */
  highlightedPlanId?: Music24PlanId | null;
}

/** The button text of a paid card: "Your plan", "Upgrade to Max" or "Switch to Plus". */
function ctaLabelOf(intl: IntlShape, tier: Music24PlanTier, currentTier: Music24PlanTier, name: string): string {
  if (tier === currentTier) {
    return intl.formatMessage(messages.yourPlan);
  }
  const upgrade = MUSIC24_TIER_RANK[tier] > MUSIC24_TIER_RANK[currentTier];
  return intl.formatMessage(upgrade ? messages.upgradeTo : messages.switchTo, { plan: name });
}

/**
 * The body of the plans page (design plans-page a): Free as one full-width row, the paid plans in
 * one row, the contact line and the compare table. Every card is for one artist, with no artist
 * stepper (R8): extra artists are sold only in Settings -> Subscription. Plug is not sold any more
 * (owner, 2026-10-23): its card shows only to a workspace that is still on Plug.
 */
const PricingPlansStepComponent: React.FC<PricingPlansStepProps> = ({
  interval,
  loadingPlanId,
  onSubscribe,
  highlightedPlanId = null,
}) => {
  const intl = useIntl();
  // Ruling 8: only the workspace owner manages billing — a member sees every card, but a dashed
  // "Owner only" slot instead of a working Subscribe/Switch button (design o5).
  const isMember = useMyTeamRole() === 'MEMBER';
  const owner = useWorkspaceOwner();
  // App Store 3.1.1 / Play billing: inside a native app's web view the plan names and features
  // stay (they are read-only), but no card carries a web price or a checkout button.
  const isNativeEmbed = useIsNativeEmbed();
  const rawPlan: Music24PlanTier = useMusic24Plan();
  const isTrialing = rawPlan === 'trial';
  // Music24's reverse trial runs at Pro level, so the Pro card — not Free — is
  // the one marked current while the trial is active.
  const currentTier: Music24PlanTier = isTrialing ? 'pro' : rawPlan;
  const paidPlans = useMemo(
    () => MUSIC24_PLANS.filter((plan) => plan.tier !== 'plug' || rawPlan === 'plug'),
    [rawPlan]
  );
  const freeHighlights = useMemo(() => planHighlights('free', intl), [intl]);
  const paidHighlights = useMemo(
    () => new Map(paidPlans.map((plan) => [plan.id, planHighlights(plan.tier, intl)])),
    [paidPlans, intl]
  );

  return (
    <div className={styles.page}>
      {isMember && owner !== null && <PricingMemberNotice owner={owner} />}
      {!isMember && isNativeEmbed && <NativeUpgradeNote className={styles.nativeNote} />}

      <PricingFreeRow
        name={intl.formatMessage(planNameMessages.free)}
        description={intl.formatMessage(planDescriptionMessages.free)}
        highlights={freeHighlights}
        isCurrent={currentTier === 'free'}
        isTrialing={isTrialing}
        hidePrice={isNativeEmbed}
      />

      <div className={styles.paid}>
        <div className={styles.plans}>
          {paidPlans.map((plan) => {
            const isCurrent = plan.tier === currentTier;
            const name = intl.formatMessage(planNameMessages[plan.tier]);
            return (
              <PricingPlanCard
                key={plan.id}
                name={name}
                description={intl.formatMessage(planDescriptionMessages[plan.tier])}
                highlightsLead={intl.formatMessage(messages.lead, {
                  plan: intl.formatMessage(planNameMessages[PLAN_BUILDS_ON[plan.tier]]),
                })}
                highlights={paidHighlights.get(plan.id) ?? []}
                interval={interval}
                monthlyAmountCents={plan.prices.monthly.amount}
                yearlyAmountCents={plan.prices.yearly.amount}
                mostPopular={plan.mostPopular}
                highlighted={plan.id === highlightedPlanId}
                isCurrent={isCurrent}
                isTrialing={isCurrent && isTrialing}
                ctaLabel={isNativeEmbed ? null : ctaLabelOf(intl, plan.tier, currentTier, name)}
                isLoading={loadingPlanId === plan.id}
                disabled={loadingPlanId !== null}
                ownerOnly={isMember && !isCurrent}
                hidePrice={isNativeEmbed}
                onSubscribe={() => onSubscribe(plan)}
              />
            );
          })}
        </div>

        <PricingContactLine />
      </div>

      <PricingCompareTable interval={interval} />
    </div>
  );
};

export const PricingPlansStep = TypedMemo(PricingPlansStepComponent);
